import { type PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import clsx from "clsx";

import { useBodyScrollLock } from "@/hooks/use-body-scroll-lock";
import { useMediaQuery } from "@/hooks/use-media-query";
import { DESKTOP_MEDIA_QUERY } from "@/styles/breakpoints";

import logoMark from "@/assets/logo-mark.svg";

import { Sidebar, SidebarCollapseToggle } from "./sidebar";

const NAV_ID = "app-nav-drawer";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Where focus goes the next time the drawer closes. Defaults to the trigger -
 * the button that opened it - and is overridden just before the two closes
 * that must NOT land there:
 *
 * - "main": a route change closed the drawer. The nav item click already moved
 *   the user to a new page; focus belongs there, not back on the hamburger.
 * - "none": the viewport crossed the desktop breakpoint while the drawer was
 *   open. The trigger is `display: none` at desktop, so focusing it would just
 *   drop focus onto <body>.
 */
type CloseFocusTarget = "trigger" | "main" | "none";

/**
 * The shell every route renders inside: a nav column, and a content column
 * beside it.
 *
 * Built on the design-system's tokens and Tailwind rather than a stylesheet of
 * its own, and shaped after studio's shell so the two tools read as one product
 * - a flex row with a sticky full-height nav column, a collapsible column at
 * desktop widths, and an off-canvas drawer below the breakpoint. Studio's own
 * shell is hand-rolled for the same reason this one is: the design-system has no
 * app-shell export, and its one sidebar component (`StudioCockpitSidebar`) is
 * shaped for grouped cockpit sections, cannot collapse, has no mobile mode, and
 * renders raw `<a href>` - which in a router SPA is a full page reload per nav
 * click.
 *
 * The breakpoint is Tailwind's `md` (48rem = 768px), which is the number
 * MOBILE_BREAKPOINT_PX already carried; `max-md:` and `md:` are mutually
 * exclusive, so the drawer rules and the column rules never fight.
 */
export function AppShell({ children }: PropsWithChildren) {
  const [navRequested, setNavRequested] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const { pathname } = useLocation();

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const closeFocusTargetRef = useRef<CloseFocusTarget>("trigger");

  // The drawer only exists below the breakpoint. Deriving `isOpen` rather than
  // storing it means widening the viewport past the breakpoint closes it for
  // free, with no resize listener racing the CSS.
  const isOpen = navRequested && !isDesktop;

  const closeNav = useCallback(() => setNavRequested(false), []);

  const openNav = useCallback(() => {
    closeFocusTargetRef.current = "trigger";
    setNavRequested(true);
  }, []);

  const toggleCollapsed = useCallback(() => setCollapsed((value) => !value), []);

  // Close on route change: a nav item was tapped, the drawer's job is done -
  // but the user just navigated, so focus belongs on the new page, not back on
  // the trigger that opened the drawer in the first place.
  useEffect(() => {
    if (navRequested) {
      closeFocusTargetRef.current = "main";
    }
    setNavRequested(false);
  }, [pathname]);

  // Crossing the breakpoint while open must clear the request outright, not
  // just let `isOpen` suspend to false while `navRequested` still sits at true.
  // Suspending it means a later rotation back below the breakpoint recomputes
  // `isOpen` to true again with no user action in between - the drawer
  // reopening itself. Clearing it here also means the close effect below never
  // tries to focus the trigger, which is `display: none` at desktop.
  useEffect(() => {
    if (isDesktop && navRequested) {
      closeFocusTargetRef.current = "none";
      setNavRequested(false);
    }
  }, [isDesktop, navRequested]);

  useEffect(() => {
    if (!isOpen) {
      // Only pull focus back if we were the ones who moved it, so the first
      // render never steals focus from the page.
      if (wasOpenRef.current) {
        wasOpenRef.current = false;
        const target = closeFocusTargetRef.current;
        closeFocusTargetRef.current = "trigger";

        if (target === "trigger") {
          triggerRef.current?.focus();
        } else if (target === "main") {
          mainRef.current?.focus();
        }
        // "none": the breakpoint crossed while open - no focus change.
      }
      return;
    }

    wasOpenRef.current = true;

    const drawer = drawerRef.current;
    const firstFocusable = drawer?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (firstFocusable ?? drawer)?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setNavRequested(false);
        return;
      }

      if (event.key !== "Tab" || !drawer) {
        return;
      }

      const focusable = Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === drawer)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Shared with any other overlay that locks body scroll (the admins create-user
  // dialog does too): see use-body-scroll-lock for why a plain per-component
  // save/restore of `overflow` is not safe once more than one can be open.
  useBodyScrollLock(isOpen);

  // The nav column is unreachable in two different ways, and both have to take
  // it out of the tab order rather than just off screen: below the breakpoint
  // when the drawer is shut, and at desktop widths when the column is collapsed
  // to nothing. Without this the links stay focusable while invisible, and Tab
  // walks into a column the user cannot see.
  const navUnreachable = isDesktop ? collapsed : !isOpen;

  return (
    <div className="bg-black flex min-h-screen text-white">
      {/* The rail that stands in for the collapsed column, carrying the only
          control that can bring it back. Desktop-only by construction: the
          collapse state is never applied below the breakpoint. */}
      {isDesktop && collapsed ? (
        <div className="sticky top-0 hidden h-screen w-12 shrink-0 flex-col items-center self-start pt-5 md:flex">
          <SidebarCollapseToggle collapsed onToggle={toggleCollapsed} controls={NAV_ID} />
        </div>
      ) : null}

      {/* The scrim. `aria-hidden` by design: it is a pointer affordance only,
          with Escape and the in-drawer close button covering the keyboard path.
          `pointer-events-none` while shut so it cannot swallow clicks on the
          page during and after the fade out. */}
      <div
        className={clsx(
          "fixed inset-0 z-50 bg-black/60 transition-opacity duration-200 md:hidden motion-reduce:transition-none",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={closeNav}
        aria-hidden="true"
      />

      <div
        id={NAV_ID}
        ref={drawerRef}
        className={clsx(
          "flex flex-col",
          // Below the breakpoint: an off-canvas drawer over the page. Vertical
          // scroll is genuine here - the drawer is height-constrained by
          // `inset-y-0`, and eight nav rows plus the profile card and logout can
          // add up to more than a phone screen. `overflow-x` is pinned to hidden
          // explicitly rather than left to the `overflow-y` side effect, because
          // this scroller is fixed-width chrome, not page content whose overflow
          // needs to stay diagnosable.
          "max-md:glass-popover max-md:border-border max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-[60] max-md:w-[min(280px,88vw)] max-md:overflow-x-hidden max-md:overflow-y-auto max-md:overscroll-contain max-md:border-r max-md:transition-transform max-md:duration-200 max-md:ease-out max-md:motion-reduce:transition-none",
          isOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full",
          // At desktop: an ordinary column, pinned full height so the page
          // scrolls underneath it, and animating its own width on collapse.
          // `overflow-hidden` is what clips the inner fixed-width panel while
          // that width runs down to zero.
          "md:sticky md:top-0 md:h-screen md:shrink-0 md:self-start md:overflow-hidden md:transition-[width] md:duration-200 md:ease-out md:motion-reduce:transition-none",
          collapsed ? "md:w-0" : "md:w-[260px]",
        )}
        // Dialog semantics are only true below the breakpoint. At desktop widths
        // this is an ordinary navigation column and must not claim to be modal.
        role={isDesktop ? undefined : "dialog"}
        aria-modal={isDesktop ? undefined : isOpen}
        aria-label={isDesktop ? undefined : "Main navigation"}
        // A closed drawer is still on screen for the length of the slide-out, so
        // without this Tab lands on a nav link inside it. `inert` takes the whole
        // subtree out of the tab order and the accessibility tree the instant it
        // closes, so Tab from the trigger carries on into the page. At desktop it
        // applies only to a collapsed column, never to an open one.
        inert={navUnreachable}
        tabIndex={-1}
      >
        {/* The panel keeps its own width so its contents do not reflow while the
            column around it animates shut. */}
        <div
          className={clsx(
            "flex h-full w-full flex-col md:w-[260px] md:transition-opacity md:duration-200 md:motion-reduce:transition-none",
            collapsed && "md:opacity-0",
          )}
        >
          {isDesktop ? null : (
            // The drawer covers the mobile bar, so the bar's trigger cannot be
            // the way back out. The close button belongs to the drawer, and sits
            // in the panel's non-scrolling head - the nav list below it is the
            // part that scrolls - so a long list cannot carry it off screen.
            <div className="flex justify-end px-3 pt-2">
              <button
                type="button"
                className="border-border bg-grey-100 rounded-btl-sm inline-flex size-11 cursor-pointer items-center justify-center border text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-500"
                aria-label="Close navigation menu"
                onClick={closeNav}
              >
                <X size={20} weight="bold" />
              </button>
            </div>
          )}
          <Sidebar
            onNavigate={closeNav}
            onToggleCollapse={isDesktop ? toggleCollapsed : undefined}
            collapseControls={NAV_ID}
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
         * The only mobile affordance back to navigation. `sticky` rather than
         * `fixed` keeps it in flow, so the content below needs no offset to
         * clear it and can never end up underneath it. `z-40` sits below the
         * scrim (50) and the drawer (60) so both still stack over it, and above
         * ordinary page content so that content does not show through as it
         * scrolls past underneath.
         */}
        <header className="glass-bar border-border sticky top-0 z-40 flex h-14 shrink-0 items-center gap-3 border-b px-3 md:hidden">
          <button
            ref={triggerRef}
            type="button"
            className="border-border bg-grey-100 rounded-btl-sm inline-flex size-11 cursor-pointer items-center justify-center border text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-500"
            // Opening only. The bar sits behind the drawer, so this control is
            // covered once the drawer is out - the close affordance lives inside
            // the drawer, where it can actually be tapped.
            aria-label="Open navigation menu"
            aria-expanded={isOpen}
            aria-controls={NAV_ID}
            // The drawer is modal and stacks over the whole bar while open; its own
            // scrim and Escape handling cover dismissal, so the trigger underneath
            // must drop out of the tab order and accessibility tree rather than
            // sit there unreachable-but-still-announced.
            inert={isOpen}
            onClick={openNav}
          >
            <List size={20} weight="bold" />
          </button>
          <span className="flex items-center gap-2">
            <img src={logoMark} alt="" aria-hidden className="size-5 shrink-0" />
            <span className="text-sm font-semibold tracking-[-0.02em] text-white">Admin</span>
          </span>
        </header>

        {/*
         * `tabIndex={-1}` makes this a valid script-focus target (see the route
         * change close above) without adding it to the ordinary tab order.
         *
         * No `overflow` below the breakpoint, deliberately. `overflow-y: auto`
         * does not stay on its own: with `overflow-x` still at its initial
         * `visible`, the computed value of `overflow-x` becomes `auto` too. That
         * turned <main> into a horizontal scroll container, and since the column
         * is never height-constrained the vertical scroll it was written for
         * never engaged - the only thing it ever did was swallow horizontal
         * overflow, making every mobile width bug invisible at once. Below the
         * breakpoint, anything that does not fit must push the page sideways
         * where it can be seen and fixed; `min-w-0` lets the column shrink to
         * the viewport instead of being propped open by its contents.
         *
         * Above the breakpoint the containment is restored, scoped explicitly
         * with `overflow-x` rather than leaning on that side effect, because
         * desktop content genuinely does overflow the column on some routes
         * (e.g. /content) and that overflow must stay inside the column rather
         * than dragging the whole shell, sidebar included, sideways. That
         * underlying overflow is a pre-existing bug in the affected routes;
         * containing it here is not fixing it, only keeping it where it was
         * already being caught. src/styles/no-hidden-overflow.test.ts pins this.
         */}
        <main
          ref={mainRef}
          className="flex min-w-0 flex-1 justify-start px-3 pt-4 pb-6 md:overflow-x-auto md:px-1 md:py-8"
          tabIndex={-1}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
